import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AppPaths from '../../AppPaths';
import { QUIZ_URL_TRIGGER } from '../../lib/fiQuiz';
import { HARDWARE_FIRST_VARIANT, parseQuizVariantOverride } from '../../lib/fiQuizOverride';
import HardwareFirstQuiz from './HardwareFirstQuiz';

type ShopDeviceKey = 'mini' | 's3';

interface HardwareFirstQuizApi {
  /** `trigger` names the CTA that opened the quiz, for analytics. */
  open(trigger: string): void;
  close(): void;
  /** Set when the quiz's result CTA is tapped while the visitor is on the shop. */
  pendingDevice: ShopDeviceKey | null;
  /** Applies `pendingDevice` once, so a re-render does not re-select it. */
  consumePendingDevice(): void;
}

const noop: HardwareFirstQuizApi = {
  open: () => undefined,
  close: () => undefined,
  pendingDevice: null,
  consumePendingDevice: () => undefined,
};

const HardwareFirstQuizContext = createContext<HardwareFirstQuizApi>(noop);

/**
 * Mounts the quiz sheet once for the whole app so any CTA can open it without
 * rendering a copy of its own. `useLaunchQuiz` is the only caller that should
 * decide *whether* this quiz is the right one for a visitor.
 */
export function HardwareFirstQuizProvider({ children }: { children: React.ReactNode }) {
  const [trigger, setTrigger] = useState<string | null>(null);
  const { search, pathname } = useLocation();
  const history = useHistory();
  const [pendingDevice, setPendingDevice] = useState<ShopDeviceKey | null>(null);
  const isOpen = trigger !== null;

  const open = useCallback((source: string) => setTrigger(source), []);
  const close = useCallback(() => setTrigger(null), []);

  // Opens the sheet on arrival for `?quizVariant=hardware-first`, so it can be
  // exercised in prod before it is wired into the CTAs.
  useEffect(() => {
    if (parseQuizVariantOverride(search) === HARDWARE_FIRST_VARIANT) {
      setTrigger(QUIZ_URL_TRIGGER);
    }
  }, [search]);

  // The shop holds every tracker on one page. A visitor already on it gets an
  // in-place selection - pushing `?device=` would not re-select, since the page
  // reads that param only on mount. From anywhere else, navigate.
  const continueWithDevice = useCallback(
    (deviceKey: ShopDeviceKey) => {
      close();
      if (pathname.endsWith(AppPaths.Shop)) {
        setPendingDevice(deviceKey);
        return;
      }
      history.push({ pathname: AppPaths.Shop, search: `?device=${deviceKey}` });
    },
    [close, history, pathname],
  );

  const consumePendingDevice = useCallback(() => setPendingDevice(null), []);

  const api = useMemo<HardwareFirstQuizApi>(
    () => ({ open, close, pendingDevice, consumePendingDevice }),
    [open, close, pendingDevice, consumePendingDevice],
  );

  return (
    <HardwareFirstQuizContext.Provider value={api}>
      {children}
      <HardwareFirstQuiz open={isOpen} onClose={close} onContinue={continueWithDevice} />
    </HardwareFirstQuizContext.Provider>
  );
}

export function useHardwareFirstQuiz(): HardwareFirstQuizApi {
  return useContext(HardwareFirstQuizContext);
}
